'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';
import type { SHARE_CARD_DETAILS_FORM_TYPE } from '@/app/(main)/client-detail/components/ClientDetailsHeader/modals/ShareViaEmailModal/consts';
import { getDefaultMessageTemplateByForms } from '@/app/(main)/client-detail/components/ClientDetailsHeader/utils/getShareModalTemplateMessage';
import type { ClientExportScopeFilters } from '@/app/(main)/client-detail/consts/clientExportScope';
import { useCreateAirRequestMutation } from '@/app/(main)/client-detail/hooks/useCreateAirRequestMutation';
import { useShareClient } from '@/app/(main)/client-detail/hooks/useShareClient';
import { displayedEmail } from '@/app/(main)/client-detail/utils/contacts';
import {
  CLIENT_SELECTION_ACTION,
  CLIENT_SELECTION_ACTION_SELECTION_MODE,
  SELECT_CLIENT_MODAL_COPY,
  type ClientSelectionAction,
} from '@/app/(main)/clients/const/clientSelection';
import { getAirRequestPath } from '@/consts/paths-utils';
import { useFetch } from '@/hooks/useFetch';
import { useSearchQuery } from '@/hooks/useSearchQuery';
import type { GetClientResponse } from '@/types/clientDetailsApiTypes';
import { QUICK_ADD_FROM_AIR_REQUEST_QUERY_KEY } from '../../const/clients';

const SEND_FORM_TYPES: SHARE_CARD_DETAILS_FORM_TYPE[] = ['profile', 'credit_card'];

export type { ClientSelectionAction };

type UseClientSelectionActionsOptions = {
  onAirRequestClientSelected?: (clientId: number) => void;
  onSendFormsClientSelected?: (clientId: number) => void;
  /** Opens export settings for the given client IDs instead of downloading immediately (SFC-1007). */
  onExportClientsSelected?: (clientIds: number[]) => void;
  onExportAllScopeSelected?: (scopeFilters: ClientExportScopeFilters) => void;
  exportListScopeFilters?: ClientExportScopeFilters;
};

export const useClientSelectionActions = (options: UseClientSelectionActionsOptions = {}) => {
  const {
    onAirRequestClientSelected,
    onSendFormsClientSelected,
    onExportClientsSelected,
    onExportAllScopeSelected,
    exportListScopeFilters,
  } = options;
  const router = useRouter();
  const [, updateSearchQuery] = useSearchQuery();
  const [activeAction, setActiveAction] = useState<ClientSelectionAction | null>(null);
  const { doFetch: getClient } = useFetch<void, GetClientResponse>();
  const { mutateAsync: createAirRequest, isPending: isCreateAirRequestPending } = useCreateAirRequestMutation();
  const { mutateAsync: shareClient, isPending: isShareClientPending } = useShareClient();

  const closeClientSelection = useCallback(() => {
    setActiveAction(null);
  }, []);

  const startQuickAddFromAirRequest = useCallback(() => {
    updateSearchQuery({ [QUICK_ADD_FROM_AIR_REQUEST_QUERY_KEY]: 'true' });
  }, [updateSearchQuery]);

  const openAirRequestClientSelection = useCallback(() => {
    setActiveAction(CLIENT_SELECTION_ACTION.AIR_REQUEST);
  }, []);

  const openSendFormsClientSelection = useCallback(() => {
    setActiveAction(CLIENT_SELECTION_ACTION.SEND_FORMS);
  }, []);

  const openExportClientSelection = useCallback(() => {
    setActiveAction(CLIENT_SELECTION_ACTION.EXPORT_CLIENT);
  }, []);

  const executeClientSelectionAction = useCallback(
    async (action: ClientSelectionAction, client_id: number) => {
      if (action === CLIENT_SELECTION_ACTION.AIR_REQUEST) {
        const response = await createAirRequest({ clientId: client_id });

        if (!response.data.id) {
          throw new Error('Air request response did not include an id');
        }

        router.push(getAirRequestPath(String(response.data.id), String(client_id)));
        return;
      }

      if (action === CLIENT_SELECTION_ACTION.SEND_FORMS) {
        const clientResponse = await getClient(`/api/clients/${client_id}`);
        const client = clientResponse.data;

        const email = displayedEmail(client);
        if (!email) {
          throw new Error('Selected client does not have an email');
        }

        await shareClient({
          clientId: client_id,
          data: {
            share_profile: true,
            share_credit_card: true,
            email,
            message: getDefaultMessageTemplateByForms(SEND_FORM_TYPES),
          },
        });
        return;
      }

      if (action === CLIENT_SELECTION_ACTION.EXPORT_CLIENT) {
        throw new Error('Use handleSelectClients for export');
      }
    },
    [createAirRequest, getClient, router, shareClient]
  );

  const handleSelectClients = useCallback(
    async ({ client_ids }: { client_ids: number[] }) => {
      if (!activeAction || client_ids.length === 0) return;

      if (activeAction === CLIENT_SELECTION_ACTION.EXPORT_CLIENT) {
        if (!onExportClientsSelected) {
          throw new Error('Export settings handler is not configured');
        }
        onExportClientsSelected(client_ids);
        return;
      }

      if (client_ids.length !== 1) {
        throw new Error('This action requires exactly one client');
      }

      const [clientId] = client_ids;

      if (activeAction === CLIENT_SELECTION_ACTION.AIR_REQUEST && onAirRequestClientSelected) {
        onAirRequestClientSelected(clientId);
        return;
      }

      if (activeAction === CLIENT_SELECTION_ACTION.SEND_FORMS && onSendFormsClientSelected) {
        onSendFormsClientSelected(clientId);
        return;
      }

      await executeClientSelectionAction(activeAction, clientId);
    },
    [
      activeAction,
      executeClientSelectionAction,
      onAirRequestClientSelected,
      onExportClientsSelected,
      onSendFormsClientSelected,
    ]
  );

  const modalProps = useMemo(() => {
    if (activeAction == null) return null;

    const isSubmitting =
      activeAction === CLIENT_SELECTION_ACTION.AIR_REQUEST
        ? isCreateAirRequestPending
        : activeAction === CLIENT_SELECTION_ACTION.SEND_FORMS
          ? isShareClientPending
          : false;

    const handsOffToModal =
      (activeAction === CLIENT_SELECTION_ACTION.AIR_REQUEST && !!onAirRequestClientSelected) ||
      (activeAction === CLIENT_SELECTION_ACTION.SEND_FORMS && !!onSendFormsClientSelected);

    return {
      isOpen: true,
      isSubmitting,
      onClose: closeClientSelection,
      onSelectClients: handleSelectClients,
      onSuccess: closeClientSelection,
      selectionMode: CLIENT_SELECTION_ACTION_SELECTION_MODE[activeAction],
      ...SELECT_CLIENT_MODAL_COPY[activeAction],
      ...(handsOffToModal ? { successMessage: undefined } : {}),
      ...(activeAction === CLIENT_SELECTION_ACTION.AIR_REQUEST
        ? { onAddNewClient: startQuickAddFromAirRequest, stackActionsOnMobile: true }
        : {}),
      ...(activeAction === CLIENT_SELECTION_ACTION.EXPORT_CLIENT && onExportAllScopeSelected
        ? {
            enableSelectAllScope: true,
            exportListScopeFilters,
            onSelectAllScope: ({ search }: { search: string }) => {
              const modalName = search.trim() || undefined;
              const scopeName = exportListScopeFilters?.name?.trim() || undefined;
              onExportAllScopeSelected({
                ...exportListScopeFilters,
                name: modalName ?? scopeName,
              });
            },
          }
        : {}),
    };
  }, [
    activeAction,
    closeClientSelection,
    exportListScopeFilters,
    handleSelectClients,
    isCreateAirRequestPending,
    isShareClientPending,
    onAirRequestClientSelected,
    onExportAllScopeSelected,
    onSendFormsClientSelected,
    startQuickAddFromAirRequest,
  ]);

  return {
    modalProps,
    openAirRequestClientSelection,
    openSendFormsClientSelection,
    openExportClientSelection,
    isCreateAirRequestPending,
    isShareClientPending,
  };
};
